import QRCode from 'qrcode';

const NANOUK_STATE_IMG = {
    fit: '/nanouk/energy_fit.png',
    balanced: '/nanouk/energy_balanced.png',
    light: '/nanouk/energy_light.png',
    heavy: '/nanouk/energy_heavy.png',
    crushed: '/nanouk/energy_crushed.png',
};

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

export async function generateWeeklyShareImage(report, language) {
    const de = language === 'de';
    const W = 1080, H = 1350;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    const font = (weight, size) => `${weight} ${size}px -apple-system, 'Segoe UI', Roboto, sans-serif`;

    // Background
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, '#FFF9EF');
    bg.addColorStop(1, '#FDEBD2');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Header
    ctx.textAlign = 'center';
    ctx.fillStyle = '#B45309';
    ctx.font = font('800', 44);
    ctx.fillText('BESTBUDDY', W / 2, 92);
    ctx.fillStyle = '#1F2937';
    ctx.font = font('800', 64);
    ctx.fillText(de ? 'Mein Wochenrückblick' : 'My Weekly Review', W / 2, 172);
    ctx.fillStyle = '#6B7280';
    ctx.font = font('500', 34);
    ctx.fillText(`${report.week_range.start} – ${report.week_range.end}`, W / 2, 224);

    // Nanouk
    const state = report.taler?.nanouk_state || 'balanced';
    try {
        const nanouk = await loadImage(NANOUK_STATE_IMG[state]);
        const nh = 330;
        const nw = nanouk.width * (nh / nanouk.height);
        ctx.drawImage(nanouk, (W - nw) / 2, 258, nw, nh);
    } catch (_e) { /* image optional */ }

    // Stats cards 2x2
    const stats = report.stats;
    const cards = [
        { emoji: '💪', value: `${stats.workouts.this_week}`, label: de ? 'Workouts' : 'Workouts', sub: `${stats.workouts.total_duration_min} min` },
        { emoji: '😴', value: `${stats.sleep.avg_per_night}h`, label: de ? 'Schlaf Ø' : 'Sleep avg', sub: de ? 'pro Nacht' : 'per night' },
        { emoji: '💧', value: `${(stats.water.daily_avg_ml / 1000).toFixed(1)}L`, label: de ? 'Wasser Ø' : 'Water avg', sub: de ? 'pro Tag' : 'per day' },
        { emoji: '🔥', value: `${stats.calories.daily_avg.toLocaleString(de ? 'de-CH' : 'en-GB')}`, label: de ? 'Kalorien Ø' : 'Calories avg', sub: 'kcal' },
    ];
    const cw = 460, ch = 200, gap = 32;
    const gridX = (W - cw * 2 - gap) / 2;
    let gridY = 620;
    cards.forEach((c, i) => {
        const x = gridX + (i % 2) * (cw + gap);
        const y = gridY + Math.floor(i / 2) * (ch + gap);
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        roundRect(ctx, x, y, cw, ch, 28);
        ctx.fill();
        ctx.textAlign = 'left';
        ctx.font = font('500', 56);
        ctx.fillText(c.emoji, x + 34, y + 84);
        ctx.fillStyle = '#111827';
        ctx.font = font('800', 58);
        ctx.fillText(c.value, x + 126, y + 86);
        ctx.fillStyle = '#6B7280';
        ctx.font = font('600', 30);
        ctx.fillText(c.label, x + 34, y + 150);
        ctx.font = font('400', 26);
        ctx.fillText(c.sub, x + 34, y + 184);
    });

    // Taler banner
    const bannerY = gridY + ch * 2 + gap + 32;
    if (report.taler?.enabled && report.taler.tracked_days > 0) {
        const t = report.taler;
        ctx.fillStyle = t.week_balance >= 0 ? '#DCFCE7' : '#FEE2E2';
        roundRect(ctx, gridX, bannerY, cw * 2 + gap, 100, 26);
        ctx.fill();
        ctx.textAlign = 'center';
        ctx.fillStyle = t.week_balance >= 0 ? '#166534' : '#991B1B';
        ctx.font = font('800', 42);
        const balanceText = t.week_balance >= 0
            ? (de ? `🪙 ${t.week_balance} Taler gespart · Ø ${t.avg_net}/${t.budget}` : `🪙 ${t.week_balance} Taler saved · avg ${t.avg_net}/${t.budget}`)
            : (de ? `🪙 ${Math.abs(t.week_balance)} Taler über Budget · Ø ${t.avg_net}/${t.budget}` : `🪙 ${Math.abs(t.week_balance)} Taler over budget · avg ${t.avg_net}/${t.budget}`);
        ctx.fillText(balanceText, W / 2, bannerY + 64);
    }

    // Invite card with QR code ("start with Nanouk too")
    const inviteY = bannerY + 128;
    try {
        const appUrl = window.location.origin;
        const qrDataUrl = await QRCode.toDataURL(appUrl, { width: 240, margin: 1, color: { dark: '#1F2937', light: '#FFFFFF' } });
        const qrImg = await loadImage(qrDataUrl);
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        roundRect(ctx, gridX, inviteY, cw * 2 + gap, 150, 26);
        ctx.fill();
        ctx.drawImage(qrImg, gridX + 24, inviteY + 15, 120, 120);
        ctx.textAlign = 'left';
        ctx.fillStyle = '#111827';
        ctx.font = font('800', 38);
        ctx.fillText(de ? 'Auch mit Nanouk starten? 🐺' : 'Start with Nanouk too? 🐺', gridX + 172, inviteY + 66);
        ctx.fillStyle = '#6B7280';
        ctx.font = font('500', 28);
        ctx.fillText(de ? 'QR scannen & Buddy werden' : 'Scan the QR & become a buddy', gridX + 172, inviteY + 110);
    } catch (_e) { /* QR optional */ }

    // Footer
    ctx.textAlign = 'center';
    ctx.fillStyle = '#9CA3AF';
    ctx.font = font('500', 28);
    ctx.fillText(de ? 'Getrackt mit BESTBUDDY & Husky Nanouk 🐾' : 'Tracked with BESTBUDDY & husky Nanouk 🐾', W / 2, H - 32);

    return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

export async function shareWeeklyImage(report, language) {
    const de = language === 'de';
    const blob = await generateWeeklyShareImage(report, language);
    if (!blob) throw new Error('image generation failed');
    const file = new File([blob], 'bestbuddy-woche.png', { type: 'image/png' });
    const shareData = {
        files: [file],
        title: de ? 'Mein Wochenrückblick' : 'My weekly review',
        text: de ? 'Meine Woche mit BESTBUDDY & Nanouk 🐺' : 'My week with BESTBUDDY & Nanouk 🐺',
    };
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share(shareData);
        return 'shared';
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bestbuddy-woche.png';
    a.click();
    URL.revokeObjectURL(url);
    return 'downloaded';
}
